import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getToken, isTokenExpired, removeToken } from "../lib/api";
import {
  handleSignIn,
  handleLogout,
  handleGoogleSignIn,
  handleAppleSignIn,
} from "../lib/authService";
import { replayGuestLikes } from "../lib/feedService";
import { openAppStore } from "../lib/appStores";

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // Website signup/login is disabled — auth lives in the native apps.
  // `openLogin` still exists so legacy call sites send users to the store.
  const [loginOpen, setLoginOpen] = useState(false);
  const [loginNotice, setLoginNotice] = useState("");

  const openLogin = useCallback((_notice = "") => {
    openAppStore();
  }, []);

  const closeLogin = useCallback(() => {
    setLoginOpen(false);
    setLoginNotice("");
  }, []);

  useEffect(() => {
    const checkAuthStatus = async () => {
      try {
        const token = getToken();
        const storedUser = localStorage.getItem("pagent_user");

        if (token && !isTokenExpired(token) && storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
        } else {
          localStorage.removeItem("pagent_user");
          removeToken();
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Failed to check auth status:", error);
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        setLoading(false);
      }
    };

    checkAuthStatus();
  }, []);

  const login = async (usernamelogin, password) => {
    try {
      const result = await handleSignIn(
        { usernamelogin, password },
        () => {},
        () => {},
        () => {}
      );

      if (result.success) {
        const storedUser = localStorage.getItem("pagent_user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
        }
        // Best-effort: persist any likes the user tapped while logged out.
        replayGuestLikes().catch(() => {});
      }

      return result;
    } catch (error) {
      console.error("Login failed:", error);
      return { success: false, error: "Login failed" };
    }
  };

  const loginWithGoogle = async (idToken) => {
    try {
      const result = await handleGoogleSignIn(
        { idToken },
        () => {},
        () => {},
        () => {}
      );
      if (result.success) {
        const storedUser = localStorage.getItem("pagent_user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
        }
        replayGuestLikes().catch(() => {});
      }
      return result;
    } catch (error) {
      console.error("Google login failed:", error);
      return { success: false, error: "Google login failed" };
    }
  };

  const loginWithApple = async (idToken) => {
    try {
      const result = await handleAppleSignIn(
        { idToken },
        () => {},
        () => {},
        () => {}
      );
      if (result.success) {
        const storedUser = localStorage.getItem("pagent_user");
        if (storedUser) {
          const userData = JSON.parse(storedUser);
          setUser(userData);
          setIsAuthenticated(true);
        }
        replayGuestLikes().catch(() => {});
      }
      return result;
    } catch (error) {
      console.error("Apple login failed:", error);
      return { success: false, error: "Apple login failed" };
    }
  };

  // Re-reads the stored session (token + pagent_user) into context. The signup
  // wizard stores tokens/profile itself (after OTP verification and after the
  // final create-profile call) and then syncs auth state through this instead
  // of forcing a reload.
  const syncFromStorage = useCallback(() => {
    try {
      const token = getToken();
      const storedUser = localStorage.getItem("pagent_user");
      if (token && !isTokenExpired(token) && storedUser) {
        setUser(JSON.parse(storedUser));
        setIsAuthenticated(true);
        replayGuestLikes().catch(() => {});
        return true;
      }
    } catch (error) {
      console.error("Failed to sync auth from storage:", error);
    }
    return false;
  }, []);

  const logout = async () => {
    // Drop context auth state before the API round-trip so route gates (like
    // the feed's incomplete-profile redirect) treat the user as signed out on
    // the very next render — e.g. abandoning signup via "Back to feed"
    // navigates in the same click. handleLogout still clears the stored
    // tokens once the API call (which needs them) settles.
    setUser(null);
    setIsAuthenticated(false);
    try {
      await handleLogout(() => {});
    } catch (error) {
      console.error("Logout API error:", error);
    }
  };

  const value = {
    user,
    loading,
    isAuthenticated,
    login,
    loginWithGoogle,
    loginWithApple,
    logout,
    syncFromStorage,
    loginOpen,
    loginNotice,
    openLogin,
    closeLogin,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
